import { type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Drawer } from "@/shared/ui/drawer";

import {
  useListings,
  useListingsSummary,
  useRefreshListings,
  type MarketplaceListing,
} from "../api/use-listings";
import { MARKETPLACE_LABEL_KEY } from "../lib/labels";
import { relativeTime } from "../lib/relative-time";
import type { MarketplaceId } from "../lib/types";

interface ListingsDrawerProps {
  open: boolean;
  onClose: () => void;
  mkp: MarketplaceId;
}

const PROBLEM_KEY: Record<"unmapped" | "duplicate", string> = {
  unmapped: "listings.problem.unmapped",
  duplicate: "listings.problem.duplicate",
};

export function ListingsDrawer({ open, onClose, mkp }: ListingsDrawerProps): JSX.Element {
  const { t, i18n } = useTranslation("marketplace_connections");
  const { data: summary } = useListingsSummary(mkp, open);
  const hasIssues = Boolean(summary && (summary.unmapped > 0 || summary.duplicate > 0));
  // Two filtered queries, not one unfiltered page: the listings endpoint is
  // paginated (default 100) and nothing sorts problem rows first, so an
  // unfiltered read can miss every issue row on a big shop (review C3).
  const { data: unmappedRows } = useListings(mkp, "unmapped", open && hasIssues);
  const { data: duplicateRows } = useListings(mkp, "duplicate", open && hasIssues);
  const refresh = useRefreshListings(mkp);

  const isWarningRow = (
    row: MarketplaceListing,
  ): row is MarketplaceListing & { status: "unmapped" | "duplicate" } =>
    row.status === "unmapped" || row.status === "duplicate";
  const warningRows = [...(unmappedRows?.items ?? []), ...(duplicateRows?.items ?? [])].filter(
    isWarningRow,
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span data-testid="listings-drawer-title">
          {t("listings.title", { mkp: t(MARKETPLACE_LABEL_KEY[mkp]) })}
        </span>
      }
      width={640}
    >
      <div className="flex items-center justify-between px-5 pt-4">
        <p data-testid="listings-summary-line" className="text-sm text-[var(--text-secondary)]">
          {summary
            ? `${t("listings.summary_line", { mapped: summary.mapped })} · ${t("listings.refreshed", { rel: relativeTime(summary.ran_at, i18n.language) })}`
            : t("listings.never")}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            refresh.mutate(undefined, {
              onSuccess: (result) =>
                toast.success(
                  t("listings.toast_refresh_success", {
                    mapped: result.mapped,
                    unmapped: result.unmapped,
                    duplicate: result.duplicate,
                  }),
                ),
              onError: (error) => toast.error(apiErrorMessage(error)),
            })
          }
          disabled={refresh.isPending}
        >
          {t("listings.refresh")}
        </Button>
      </div>

      {warningRows.length > 0 && (
        <div className="px-5 py-4" data-testid="listings-warning-block">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--row-divider)] text-[var(--text-subtle)]">
                <th className="py-2 pr-3 font-medium">{t("listings.col.seller_sku")}</th>
                <th className="py-2 pr-3 font-medium">{t("listings.col.status")}</th>
                <th className="py-2 pr-3 font-medium">{t("listings.col.problem")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--row-divider)]">
              {warningRows.map((row, i) => (
                <tr
                  key={`${row.product_id}-${row.variant_id}-${i}`}
                  data-testid="listings-warning-row"
                  title={t("listings.ids_tooltip", {
                    productId: row.product_id,
                    variantId: row.variant_id,
                  })}
                >
                  <td className="py-2 pr-3">{row.seller_sku}</td>
                  <td className="py-2 pr-3">{t(`listings.status.${row.status}`)}</td>
                  <td className="py-2 pr-3">{t(PROBLEM_KEY[row.status])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
